import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import Modal from '../Modal';
import UserEmailSelect from '../UserEmailSelect';
import { Clock, Calendar, AlertCircle, Loader2, Info, Link } from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase';
import { getFirestore, doc, getDoc, updateDoc } from 'firebase/firestore';
import Button from '../Button';

const ScheduleQueryModal = ({
  isOpen,
  onClose,
  savedQueryId,
  savedQueryName,
  scheduledQueryId = null, // Add a prop for the scheduled query ID when editing
  initialData = null // Add a prop for initial data when editing
}) => {
  const { currentUser, isAdmin } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [sharingStatus, setSharingStatus] = useState('idle'); // 'idle', 'loading', 'success', 'error'
  const [scheduleData, setScheduleData] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    frequency: 'daily',
    hour: '09',
    minute: '00',
    dayOfWeek: '1', // Monday
    dayOfMonth: '1',
    recipients: [],
    format: 'csv',
    enabled: true,
    timeZoneOffset: -new Date().getTimezoneOffset() // Get local timezone offset in minutes (reversed for API)
  });
  const [errors, setErrors] = useState({});
  const [saveStatus, setSaveStatus] = useState(null);
  const db = getFirestore();

  // On initial mount, check if we're in edit mode and have initial data
  useEffect(() => {
    if (isOpen) {
      if (initialData) {
        // We're in edit mode and have initial data
        initializeWithData(initialData);
      } else if (scheduledQueryId) {
        // We have a scheduledQueryId but no initial data, so load it
        loadExistingSchedule();
      } else {
        // We're in create mode, just set the default name
        setFormData(prev => ({
          ...prev,
          name: savedQueryName ? `${savedQueryName} Report` : prev.name
        }));
      }
    }
  }, [isOpen, savedQueryName, scheduledQueryId, initialData]);

  // Initialize form with provided data
  const initializeWithData = (data) => {
    console.log('Initializing form with data:', data);
    
    setScheduleData(data);
    
    // Get schedule data
    const schedule = data.schedule || {};
    
    setFormData({
      name: data.name || '',
      frequency: schedule.frequency || 'daily',
      hour: String(schedule.hour || 9).padStart(2, '0'),
      minute: String(schedule.minute || 0).padStart(2, '0'),
      dayOfWeek: String(schedule.dayOfWeek || 1),
      dayOfMonth: String(schedule.dayOfMonth || 1),
      recipients: data.recipients || [],
      format: data.format || 'csv',
      enabled: data.enabled !== undefined ? data.enabled : true,
      timeZoneOffset: schedule.timeZoneOffset !== undefined 
        ? schedule.timeZoneOffset 
        : -new Date().getTimezoneOffset() // Reversed for API compatibility
    });
  };

  const loadExistingSchedule = async () => {
    if (!currentUser?.email || !isAdmin) return;

    try {
      setIsLoading(true);
      const getScheduledQueries = httpsCallable(functions, 'getScheduledQueries');
      const result = await getScheduledQueries({
        userEmail: currentUser.email,
        savedQueryId
      });

      if (result.data.success && result.data.scheduledQueries.length > 0) {
        // Find the specific scheduled query
        const schedule = result.data.scheduledQueries.find(q => q.id === scheduledQueryId) || 
                          result.data.scheduledQueries[0];
                          
        console.log('Loaded schedule data:', schedule);
        
        setScheduleData(schedule);
        
        // Populate form with existing data
        setFormData({
          name: schedule.name,
          frequency: schedule.schedule.frequency,
          hour: String(schedule.schedule.hour || 9).padStart(2, '0'),
          minute: String(schedule.schedule.minute || 0).padStart(2, '0'),
          dayOfWeek: String(schedule.schedule.dayOfWeek || 1),
          dayOfMonth: String(schedule.schedule.dayOfMonth || 1),
          recipients: schedule.recipients || [],
          format: schedule.format || 'csv',
          enabled: schedule.enabled !== undefined ? schedule.enabled : true,
          timeZoneOffset: schedule.schedule.timeZoneOffset !== undefined 
            ? schedule.schedule.timeZoneOffset 
            : -new Date().getTimezoneOffset() // Reversed for API compatibility
        });
      }
    } catch (error) {
      console.error('Error loading schedule:', error);
      setSaveStatus({
        type: 'error',
        message: 'Failed to load existing schedule'
      });
    } finally {
      setIsLoading(false);
    }
  };

  // New function to ensure query is shared via URL
  const ensureQueryIsShared = async () => {
    if (!savedQueryId || !currentUser?.email) {
      return false;
    }

    try {
      setSharingStatus('loading');
      
      // Get the saved query to find the original LLM Query ID
      const savedQueryRef = doc(db, 'SavedQueries', savedQueryId);
      const savedQueryDoc = await getDoc(savedQueryRef);
      
      if (!savedQueryDoc.exists()) {
        console.error('Saved query not found:', savedQueryId);
        setSharingStatus('error');
        return false;
      }
      
      const savedQueryData = savedQueryDoc.data();
      const llmQueryId = savedQueryData.documentId || savedQueryData.originalLlmQueryId;
      
      if (!llmQueryId) {
        console.error('LLM Query ID not found in saved query');
        setSharingStatus('error');
        return false;
      }
      
      // Update the LLM Query to mark it as shared
      const llmQueryRef = doc(db, 'LLM Queries', llmQueryId);
      const llmQueryDoc = await getDoc(llmQueryRef);
      
      if (!llmQueryDoc.exists()) {
        console.error('LLM Query not found:', llmQueryId);
        setSharingStatus('error');
        return false;
      }
      
      // Mark the query as shared by updating Firestore
      await updateDoc(llmQueryRef, {
        sharedByLink: true,
        urlShared: true,
        urlSharedAt: new Date(),
        urlSharedBy: currentUser.email || 'scheduled-report'
      });
      
      console.log('Query successfully marked as shared:', llmQueryId);
      setSharingStatus('success');
      return true;
    } catch (error) {
      console.error('Error sharing query:', error);
      setSharingStatus('error');
      return false;
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Report name is required';
    }

    if (formData.recipients.length === 0) {
      newErrors.recipients = 'At least one recipient is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!currentUser?.email) {
      setSaveStatus({
        type: 'error',
        message: 'You must be logged in to schedule queries'
      });
      return;
    }
  
    if (!validateForm()) return;
    
    // First, ensure the query is shared via URL
    const sharingSuccessful = await ensureQueryIsShared();
    
    if (!sharingSuccessful) {
      setSaveStatus({
        type: 'error',
        message: 'Failed to prepare query for sharing. Please try again.'
      });
      return;
    }
  
    try {
      setIsLoading(true);
      const manageScheduledQuery = httpsCallable(functions, 'manageScheduledQuery');
  
      // Always calculate the timezone offset fresh before submission
      // For UTC+3, this should be 180 (positive 180 minutes)
      const browserTimezoneOffset = -new Date().getTimezoneOffset();
      console.log('Browser timezone name:', Intl.DateTimeFormat().resolvedOptions().timeZone);
      console.log('Browser timezone offset (minutes):', browserTimezoneOffset);
  
      // Prepare schedule object
      const schedule = {
        frequency: formData.frequency,
        minute: parseInt(formData.minute, 10),
        hour: parseInt(formData.hour, 10),
        timeZoneOffset: browserTimezoneOffset // Use the freshly calculated value
      };
  
      // Add the appropriate day field based on frequency
      if (formData.frequency === 'weekly') {
        schedule.dayOfWeek = parseInt(formData.dayOfWeek, 10);
      } else if (formData.frequency === 'monthly') {
        schedule.dayOfMonth = parseInt(formData.dayOfMonth, 10);
      }
  
      // Determine if we're creating or updating
      const action = scheduleData || scheduledQueryId ? 'update' : 'create';
      
      const params = {
        userEmail: currentUser.email,
        action,
        savedQueryId,
        name: formData.name,
        schedule, // Includes the fresh timezone offset
        recipients: formData.recipients,
        format: formData.format,
        enabled: formData.enabled,
        // Also send it at the top level for debugging
        timeZoneOffset: browserTimezoneOffset
      };
  
      console.log('Submitting schedule with params:', params);
  
      // Add the scheduledQueryId if we're updating
      if (action === 'update') {
        params.scheduledQueryId = scheduledQueryId || (scheduleData ? scheduleData.id : null);
      }
  
      const result = await manageScheduledQuery(params);
      console.log('Schedule operation result:', result);
  
      if (result.data && result.data.success) {
        setSaveStatus({
          type: 'success',
          message: `Schedule ${action === 'create' ? 'created' : 'updated'} successfully`
        });
        
        // Close modal after a delay
        setTimeout(() => {
          onClose();
          setSaveStatus(null);
        }, 1500);
      } else {
        throw new Error((result.data && result.data.error) || 'Failed to save schedule');
      }
    } catch (error) {
      console.error('Error saving schedule:', error);
      setSaveStatus({
        type: 'error',
        message: error.message || 'Failed to save schedule'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!scheduleData || !scheduleData.id) return;

    if (window.confirm('Are you sure you want to delete this schedule?')) {
      try {
        setIsLoading(true);
        const manageScheduledQuery = httpsCallable(functions, 'manageScheduledQuery');
        
        console.log('Deleting schedule with params:', {
          userEmail: currentUser.email,
          action: 'delete',
          scheduledQueryId: scheduleData.id
        });
        
        const result = await manageScheduledQuery({
          userEmail: currentUser.email,
          action: 'delete',
          scheduledQueryId: scheduleData.id
        });

        console.log('Delete operation result:', result);

        if (result.data && result.data.success) {
          setSaveStatus({
            type: 'success',
            message: 'Schedule deleted successfully'
          });
          
          // Close modal after a delay
          setTimeout(() => {
            onClose();
            setSaveStatus(null);
          }, 1500);
        } else {
          throw new Error((result.data && result.data.error) || 'Failed to delete schedule');
        }
      } catch (error) {
        console.error('Error deleting schedule:', error);
        setSaveStatus({
          type: 'error',
          message: error.message || 'Failed to delete schedule'
        });
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleManualRun = async () => {
    const queryId = scheduleData?.id || scheduledQueryId;
    if (!queryId) return;

    try {
      setIsLoading(true);
      const triggerScheduledQuery = httpsCallable(functions, 'triggerScheduledQuery');
      
      console.log('Running schedule with params:', {
        userEmail: currentUser.email,
        scheduledQueryId: queryId
      });
      
      const result = await triggerScheduledQuery({
        userEmail: currentUser.email,
        scheduledQueryId: queryId
      });

      console.log('Run operation result:', result);

      if (result.data && result.data.success) {
        setSaveStatus({
          type: 'success',
          message: 'Query executed successfully. Reports will be sent shortly.'
        });
      } else {
        throw new Error((result.data && result.data.error) || 'Failed to execute scheduled query');
      }
    } catch (error) {
      console.error('Error executing scheduled query:', error);
      setSaveStatus({
        type: 'error',
        message: error.message || 'Failed to execute scheduled query'
      });
    } finally {
      setIsLoading(false);
    }
  };


  const isEditMode = !!(scheduleData || scheduledQueryId);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditMode ? 'Edit Schedule' : 'Schedule Query'}
      onSubmit={handleSubmit}
      isLoading={isLoading}
      confirmText={isEditMode ? 'Update Schedule' : 'Create Schedule'}
      size="lg"
      showButtons={!isEditMode} // Hide default buttons in edit mode
    >
      <div className="space-y-6">
        {saveStatus && (
          <div 
            className={`p-3 rounded-md ${
              saveStatus.type === 'success' 
                ? 'bg-green-50 text-green-800' 
                : 'bg-red-50 text-red-800'
            }`}
          >
            {saveStatus.type === 'success' ? (
              <div className="flex items-center">
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span>{saveStatus.message}</span>
              </div>
            ) : (
              <div className="flex items-center">
                <AlertCircle className="w-5 h-5 mr-2" />
                <span>{saveStatus.message}</span>
              </div>
            )}
          </div>
        )}

        {/* Add sharing info notice */}
        <div className="flex items-start p-4 border border-blue-100 rounded-md bg-blue-50">
          <Info className="w-5 h-5 text-blue-500 mt-0.5 mr-3 flex-shrink-0" />
          <div className="text-sm text-blue-700">
            <p className="font-medium mb-1">Query Sharing</p>
            <p>
              Scheduling this query will automatically make it shareable by link. 
              Recipients will receive both the data file and a link to view the query online.
            </p>
          </div>
        </div>

        {sharingStatus === 'loading' && (
          <div className="flex items-center p-3 rounded-md bg-gray-50 text-gray-600">
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            <span>Preparing query for sharing...</span>
          </div>
        )}

        {isLoading && !formData.name && (
          <div className="flex justify-center items-center py-10">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
            <span className="ml-2 text-gray-600">Loading schedule...</span>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Report Name *
          </label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({...formData, name: e.target.value})}
            className={`block w-full px-3 py-2 border ${errors.name ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary sm:text-sm`}
            placeholder="Monthly Sales Report"
          />
          {errors.name && (
            <p className="mt-1 text-sm text-red-600">{errors.name}</p>
          )}
        </div>

        <div>
          <div className="flex items-center mb-4">
            <Calendar className="w-5 h-5 text-gray-400 mr-2" />
            <span className="text-sm font-medium text-gray-700">Schedule Frequency</span>
          </div>
          
          <div className="grid grid-cols-2 gap-4 mb-4">
            {['hourly', 'daily', 'weekly', 'monthly'].map((freq) => (
              <div key={freq} className="flex items-center">
                <input
                  id={`freq-${freq}`}
                  type="radio"
                  name="frequency"
                  value={freq}
                  checked={formData.frequency === freq}
                  onChange={() => setFormData({...formData, frequency: freq})}
                  className="h-4 w-4 text-primary focus:ring-primary border-gray-300"
                />
                <label htmlFor={`freq-${freq}`} className="ml-2 block text-sm text-gray-700 capitalize">
                  {freq}
                </label>
              </div>
            ))}
          </div>

          <div className="mt-4 space-y-4">
            {/* Show time selector for all frequencies except hourly */}
            {formData.frequency !== 'hourly' && (
              <div className="flex items-center space-x-2">
                <Clock className="w-5 h-5 text-gray-400" />
                <span className="text-sm text-gray-700">Run at:</span>
                <select
                  value={formData.hour}
                  onChange={(e) => setFormData({...formData, hour: e.target.value})}
                  className="block w-20 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
                >
                  {Array.from({ length: 24 }, (_, i) => i).map((hour) => (
                    <option key={hour} value={String(hour).padStart(2, '0')}>
                      {String(hour).padStart(2, '0')}
                    </option>
                  ))}
                </select>
                <span>:</span>
                <select
                  value={formData.minute}
                  onChange={(e) => setFormData({...formData, minute: e.target.value})}
                  className="block w-20 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
                >
                  {Array.from({ length: 60 }, (_, i) => i).map((minute) => (
                    <option key={minute} value={String(minute).padStart(2, '0')}>
                      {String(minute).padStart(2, '0')}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Show day of week selector for weekly frequency */}
            {formData.frequency === 'weekly' && (
              <div className="flex items-center space-x-2">
                <Calendar className="w-5 h-5 text-gray-400" />
                <span className="text-sm text-gray-700">Day of week:</span>
                <select
                  value={formData.dayOfWeek}
                  onChange={(e) => setFormData({...formData, dayOfWeek: e.target.value})}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
                >
                  <option value="0">Sunday</option>
                  <option value="1">Monday</option>
                  <option value="2">Tuesday</option>
                  <option value="3">Wednesday</option>
                  <option value="4">Thursday</option>
                  <option value="5">Friday</option>
                  <option value="6">Saturday</option>
                </select>
              </div>
            )}

            {/* Show day of month selector for monthly frequency */}
            {formData.frequency === 'monthly' && (
              <div className="flex items-center space-x-2">
                <Calendar className="w-5 h-5 text-gray-400" />
                <span className="text-sm text-gray-700">Day of month:</span>
                <select
                  value={formData.dayOfMonth}
                  onChange={(e) => setFormData({...formData, dayOfMonth: e.target.value})}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
                >
                  {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                    <option key={day} value={String(day)}>
                      {day}
                    </option>
                  ))}
                </select>
              </div>
            )}
              <div className="mt-2 flex items-center text-sm text-gray-500">
                <Clock className="w-4 h-4 mr-1" />
                <span>
                  Times are set in your local timezone ({Intl.DateTimeFormat().resolvedOptions().timeZone})
                </span>
              </div>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            File Format
          </label>
          <div className="flex space-x-4">
            <div className="flex items-center">
              <input
                id="format-csv"
                type="radio"
                name="format"
                value="csv"
                checked={true}
                disabled={true}
                className="h-4 w-4 text-primary focus:ring-primary border-gray-300"
              />
              <label htmlFor="format-csv" className="ml-2 block text-sm text-gray-700">
                CSV
              </label>
            </div>
          </div>
          <p className="mt-1 text-xs text-gray-500">
            Query results will be delivered as CSV files.
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Recipients *
          </label>
          <UserEmailSelect
            value={formData.recipients}
            onChange={(recipients) => setFormData({...formData, recipients})}
            allowCustomEmails={true}
            placeholder="Add email addresses"
            className={errors.recipients ? 'border-red-300' : ''}
          />
          {errors.recipients && (
            <p className="mt-1 text-sm text-red-600">{errors.recipients}</p>
          )}
          <p className="mt-1 text-xs text-gray-500">
            Recipients will receive an email with the query results attached.
            Each recipient can unsubscribe using a link in the email.
          </p>
        </div>

        <div className="flex items-center">
          <input
            id="enabled"
            type="checkbox"
            checked={formData.enabled}
            onChange={(e) => setFormData({...formData, enabled: e.target.checked})}
            className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
          />
          <label htmlFor="enabled" className="ml-2 block text-sm text-gray-700">
            Active
          </label>
        </div>

        {/* Custom footer for edit mode with all buttons */}
        {isEditMode && (
          <div className="bg-gray-50 px-6 py-4 flex justify-between -mx-6 -mb-4 mt-6">
            <div className="flex space-x-2">
             <Button
                onClick={handleManualRun}
                disabled={isLoading}
                className="text-green-700 hover:text-green-800 hover:bg-green-50"
              >
                Run Now
              </Button>
              <Button
                onClick={handleDelete}
                disabled={isLoading}
                className="text-red-700 hover:text-red-800 hover:bg-red-50"
              >
                Delete Schedule
              </Button>
            </div>
            <div className="flex space-x-2">
              <Button onClick={onClose} className="mr-2">
                Cancel
              </Button>
              <Button 
                onClick={handleSubmit} 
                loading={isLoading}
              >
                Update Schedule
              </Button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default ScheduleQueryModal;